import React, { useCallback, useEffect, useRef, useState } from "react";
import { useStableId } from "@/hooks/useStableId";
import type { InputHTMLAttributes } from "react";
import eyeIcon from "@public/img/eye.svg";
import eyeOffIcon from "@public/img/eye-off.svg";
import "@/styles/text-field.css";

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  height?: string;
  radius?: string;
  rootClassName?: string;
  type?: "text" | "password" | "email" | "tel" | "search" | "number";
  ref?: React.Ref<HTMLInputElement>;
};

export default function TextField({
  id,
  label,
  error,
  className,
  rootClassName,
  icon,
  type = "text",
  dir = "rtl",
  ref,
  onChange,
  onFocus,
  onBlur,
  ...inputProps
}: TextFieldProps) {
  const generatedId = useStableId("text-field");
  const inputId = id !== undefined && id !== null && id !== "" ? id : generatedId;
  const isPasswordField = type === "password";
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (!ref) return;
      if (typeof ref === "function") {
        ref(node);
      } else {
        ref.current = node;
      }
    },
    [ref]
  );

  useEffect(() => {
    const input = inputRef.current;
    if (!input || !onChange) return;

    const notifyChange = () => {
      onChange({ target: input, currentTarget: input } as React.ChangeEvent<HTMLInputElement>);
    };

    input.addEventListener("change", notifyChange);
    const onAnimationStart = (event: AnimationEvent) => {
      if (event.animationName === "onAutoFillStart") {
        notifyChange();
      }
    };
    input.addEventListener("animationstart", onAnimationStart);
    const timers = [50, 200, 500].map((ms) => window.setTimeout(notifyChange, ms));

    return () => {
      input.removeEventListener("change", notifyChange);
      input.removeEventListener("animationstart", onAnimationStart);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [onChange]);

  const boxClass =
    "text-field__box" +
    (error ? " is-error" : "") +
    (isFocused && !error ? " is-focused" : "") +
    (className ? " " + className : "");

  const inputClass =
    "text-field__input" +
    (isPasswordField ? " has-toggle" : "") +
    (icon ? " has-icon" : "");

  return (
    <div className={"text-field " + (rootClassName ? rootClassName : "")}>
      {label ? (
        <label htmlFor={inputId} className="text-field__label">
          {label}
        </label>
      ) : null}

      <div className={boxClass}>
        {icon ? <span className="text-field__icon">{icon}</span> : null}

        <input
          id={inputId}
          dir={dir}
          type={isPasswordField && isPasswordVisible ? "text" : type}
          className={inputClass}
          {...inputProps}
          onChange={onChange}
          onFocus={(e) => {
            setIsFocused(true);
            if (onFocus) onFocus(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            if (onBlur) onBlur(e);
          }}
          ref={setInputRef}
        />

        {isPasswordField ? (
          <button
            type="button"
            aria-label={isPasswordVisible ? "مخفی کردن رمز" : "نمایش رمز"}
            onClick={() => setIsPasswordVisible((prev) => !prev)}
            className="text-field__toggle"
          >
            <img src={!isPasswordVisible ? eyeIcon : eyeOffIcon} alt="" />
          </button>
        ) : null}
      </div>

      {error ? <p className="text-field__error">{error}</p> : null}
    </div>
  );
}
